import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Phone, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULES_DAYS = (() => {
  const d = Math.ceil(
    (new Date("2027-03-01T00:00:00+11:00") - new Date()) / 864e5
  );
  return d > 0 ? d : 0;
})();

const SYSTEM_OPTIONS = [
  { value: "gas", label: "Gas" },
  { value: "electric", label: "Electric" },
  { value: "solar", label: "Solar" },
  { value: "other", label: "Other" },
];

// DOM order — used to scroll to the first field that needs attention
const ERROR_ORDER = [
  "owns_home",
  "income_under_150k",
  "has_hot_water",
  "postcode",
  "existing_system",
  "name",
  "email",
  "phone",
  "consent",
];

// Convert AU phone to +61 E.164-ish format, e.g. 0412345678 -> +61412345678
const toE164 = (raw) => {
  const cleaned = String(raw).replace(/[^\d+]/g, "");
  if (cleaned.startsWith("+61")) return cleaned;
  if (cleaned.startsWith("61")) return `+${cleaned}`;
  if (cleaned.startsWith("0")) return `+61${cleaned.slice(1)}`;
  return `+61${cleaned}`;
};

const isAuMobile = (raw) => {
  const cleaned = String(raw).replace(/[\s-]/g, "");
  return /^(04\d{8}|\+614\d{8}|614\d{8})$/.test(cleaned);
};

const InlineError = ({ id, children }) =>
  children ? (
    <p
      data-testid={`error-${id}`}
      className="mt-1 text-xs font-medium text-red-300 md:text-red-600"
      role="alert"
    >
      {children}
    </p>
  ) : null;

const inputCls =
  "w-full h-9 md:h-[42px] rounded-[10px] border px-3.5 text-sm transition-[border-color] duration-200 focus:outline-none focus:ring-2 focus:ring-amber focus:border-amber bg-white/[0.14] border-white/35 text-white placeholder:text-white/65 md:bg-[#F9FAFC] md:border-[#DDE2EA] md:text-navy-ink md:placeholder:text-navy-muted/50";

const labelCls =
  "block text-[12.5px] md:text-[13.5px] font-semibold text-white md:text-navy-ink mb-1.5";

const pillBase =
  "h-[34px] md:h-[38px] inline-flex items-center justify-center rounded-full border text-[13px] font-semibold uppercase tracking-wide transition-[background-color,color,border-color] duration-200";
const pillCls = (active) =>
  `${pillBase} ${
    active
      ? "bg-[#13AFDC] text-navy border-[#13AFDC]"
      : "bg-white/[0.12] border-white/35 text-white md:bg-[#F6F8FB] md:border-[#DDE2EA] md:text-navy md:hover:border-[#13AFDC]"
  }`;

const YesNo = ({ name, label, value, onChange, error }) => (
  <fieldset
    data-testid={`question-${name}`}
    data-error-anchor={name}
    className="flex flex-col"
  >
    <legend className={labelCls}>{label}</legend>
    <div className="flex gap-2 mt-auto">
      {["yes", "no"].map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(name, opt)}
          data-testid={`${name}-${opt}-button`}
          aria-pressed={value === opt}
          className={`${pillCls(value === opt)} px-4 md:px-5`}
        >
          {opt === "yes" ? "Yes" : "No"}
        </button>
      ))}
    </div>
    <InlineError id={name}>{error}</InlineError>
  </fieldset>
);

const HeroEligibilityCard = () => {
  const [answers, setAnswers] = useState({});
  const [fields, setFields] = useState({ name: "", email: "", phone: "" });
  const [postcode, setPostcode] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [step, setStep] = useState(1); // mobile 2-step flow (desktop shows all)

  const utm = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const captured = {
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
    };
    if (Object.values(captured).every((v) => v === null)) {
      captured.utm_source = "direct";
      captured.utm_medium = "none";
    }
    return captured;
  }, []);

  const clearError = (key) =>
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));

  const setAnswer = (key, val) => {
    setAnswers((a) => ({ ...a, [key]: val }));
    clearError(key);
  };
  const setField = (key) => (e) => {
    setFields((f) => ({ ...f, [key]: e.target.value }));
    clearError(key);
  };

  const scrollToError = (key) => {
    const el = document.querySelector(`[data-error-anchor="${key}"]`);
    if (!el) return;
    if (window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -110, duration: 0.9 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // Mobile step 1 -> 2: validate eligibility questions first
  const handleNext = () => {
    const errs = {};
    if (!answers.owns_home) errs.owns_home = "Please answer this question.";
    if (!/^3\d{3}$/.test(postcode.trim()))
      errs.postcode = "Please enter a Victorian postcode (3000–3999).";
    if (!answers.income_under_150k)
      errs.income_under_150k = "Please answer this question.";
    if (!answers.has_hot_water)
      errs.has_hot_water = "Please answer this question.";
    if (!answers.existing_system)
      errs.existing_system = "Please select your current system.";
    setErrors(errs);
    const firstKey = ERROR_ORDER.find((k) => errs[k]);
    if (firstKey) {
      scrollToError(firstKey);
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const errs = {};
    if (!answers.owns_home) errs.owns_home = "Please answer this question.";
    if (!/^3\d{3}$/.test(postcode.trim()))
      errs.postcode = "Please enter a Victorian postcode (3000–3999).";
    if (!answers.income_under_150k)
      errs.income_under_150k = "Please answer this question.";
    if (!answers.has_hot_water)
      errs.has_hot_water = "Please answer this question.";
    if (!answers.existing_system)
      errs.existing_system = "Please select your current system.";
    if (!fields.name.trim()) errs.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim()))
      errs.email = "Please enter a valid email address.";
    if (!isAuMobile(fields.phone))
      errs.phone = "Please enter a valid Australian mobile number.";
    if (!consent)
      errs.consent =
        "Please tick the consent box so we can contact you about your quote.";
    setErrors(errs);
    const firstKey = ERROR_ORDER.find((k) => errs[k]);
    if (firstKey) {
      // On mobile, jump back to step 1 if a step-1 field needs attention
      if (window.innerWidth < 768 && ["owns_home", "income_under_150k", "has_hot_water", "postcode", "existing_system"].includes(firstKey)) {
        setStep(1);
      }
      scrollToError(firstKey);
      return;
    }
    setStatus("submitting");
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...answers, ...fields, postcode: postcode.trim(), ...utm }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      if (data.webhook_delivered !== true) {
        throw new Error("Lead saved but webhook delivery failed");
      }
      if (typeof window.fbq === "function") window.fbq("track", "Lead");
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "lead_submitted",
        lead_email: fields.email.trim().toLowerCase(),
        lead_phone: toE164(fields.phone),
        lead_postcode: postcode.trim(),
        utm_source: utm.utm_source || "direct",
      });
      window.dataLayer.push({ event: "lead_submit" });
      setStatus("success");
    } catch {
      setStatus("error");
      setErrorMsg(
        "Something went wrong sending your details. Please try again, or call us directly."
      );
    }
  };

  return (
    <div
      id="quote-form"
      data-testid="hero-form-card"
      className="rounded-[18px] pt-3.5 px-3.5 pb-4 md:px-8 md:py-7 w-full border border-white/[0.28] md:border-0 bg-[#011B47]/[0.38] md:bg-[rgba(255,255,255,0.96)] backdrop-blur-[18px] md:backdrop-blur-[8px] backdrop-saturate-[1.4] shadow-[0_20px_50px_rgba(0,0,0,0.35)] md:shadow-[0_30px_80px_rgba(0,0,0,0.35)]"
    >
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="thanks"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="thank-you-message"
            className="text-center py-8"
          >
            <CheckCircle2 className="w-14 h-14 text-gdark mx-auto mb-5" />
            <h3 className="font-serif font-bold text-white md:text-navy-ink text-3xl md:text-4xl">
              Thank you — you&apos;re in the queue.
            </h3>
            <p className="mt-3 text-white/80 md:text-navy-muted text-lg md:text-xl max-w-md mx-auto">
              A C2H Energy specialist will call you within one business day to
              confirm your eligibility and quote.
            </p>
            <p className="mt-7 text-base font-semibold text-white md:text-navy-ink">
              Want to talk now?
            </p>
            <a
              href="tel:+61494686355"
              data-testid="thank-you-call-button"
              className="mt-3 inline-flex items-center gap-2 bg-amber text-navy-deep font-bold text-lg px-8 py-3.5 rounded-lg hover:-translate-y-0.5 transition-transform duration-200"
            >
              <Phone className="w-5 h-5" />
              Call 0494 686 355
            </a>
            <p
              data-testid="thank-you-finance-note"
              className="mt-6 text-sm text-white/70 md:text-navy-muted max-w-md mx-auto"
            >
              Want to spread the cost? Ask us about a 0% interest payment plan
              when we call.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            data-testid="lead-form"
            noValidate
          >
            {/* Mobile title row + progress */}
            <div className="md:hidden mb-3">
              <div className="flex items-baseline justify-between">
                <h2
                  data-testid="hero-card-heading-mobile"
                  className="font-serif font-bold text-white text-[18px] tracking-tight"
                >
                  Check Your Eligibility
                </h2>
                <span
                  data-testid="form-step-indicator"
                  className="text-[11px] text-[#C9D6E8]"
                >
                  Step {step} of 2
                </span>
              </div>
              <div
                data-testid="form-progress-bar"
                className="mt-2 h-[3px] rounded-full bg-white/25 overflow-hidden"
              >
                <div
                  className="h-full rounded-full bg-[#13AFDC] transition-[width] duration-300"
                  style={{ width: step === 1 ? "50%" : "100%" }}
                />
              </div>
            </div>

            {/* Desktop title */}
            <div className="hidden md:block mb-4">
              <h2
                data-testid="hero-card-heading"
                className="font-serif font-bold text-navy text-[26px] tracking-tight"
              >
                Check Your Eligibility
              </h2>
              <p className="mt-0.5 text-[13px] text-navy-muted">
                Takes less than 60 seconds
              </p>
            </div>

            {/* Step 1 (mobile) — eligibility questions; always visible on desktop */}
            <div
              data-testid="form-step-1"
              className={`${
                step === 1 ? "grid" : "hidden"
              } md:grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-3 md:gap-y-[18px] mb-3 md:mb-[18px]`}
            >
              <YesNo
                name="owns_home"
                label="Own your home?"
                value={answers.owns_home}
                onChange={setAnswer}
                error={errors.owns_home}
              />
              <YesNo
                name="income_under_150k"
                label="Income under $150k?"
                value={answers.income_under_150k}
                onChange={setAnswer}
                error={errors.income_under_150k}
              />
              <YesNo
                name="has_hot_water"
                label="Have hot water now?"
                value={answers.has_hot_water}
                onChange={setAnswer}
                error={errors.has_hot_water}
              />
              <div
                data-testid="question-postcode"
                data-error-anchor="postcode"
              >
                <label htmlFor="lead-postcode" className={labelCls}>
                  Postcode
                </label>
                <input
                  id="lead-postcode"
                  data-testid="lead-postcode-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  value={postcode}
                  onChange={(e) => {
                    setPostcode(
                      e.target.value.replace(/\D/g, "").slice(0, 4)
                    );
                    clearError("postcode");
                  }}
                  placeholder="3128"
                  className={inputCls}
                />
                <InlineError id="postcode">{errors.postcode}</InlineError>
              </div>
              <fieldset
                data-testid="question-existing_system"
                data-error-anchor="existing_system"
                className="col-span-2 md:col-span-2"
              >
                <legend className={labelCls}>Current system</legend>
                <div className="grid grid-cols-4 gap-1.5 md:gap-2">
                  {SYSTEM_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAnswer("existing_system", opt.value)}
                      data-testid={`existing-system-${opt.value}-button`}
                      aria-pressed={answers.existing_system === opt.value}
                      className={`${pillCls(
                        answers.existing_system === opt.value
                      )} px-1 md:px-2 text-[11px] md:text-[12px]`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <InlineError id="existing_system">
                  {errors.existing_system}
                </InlineError>
              </fieldset>
            </div>

            {/* Mobile Next button */}
            {step === 1 && (
              <button
                type="button"
                onClick={handleNext}
                data-testid="lead-next-button"
                className="md:hidden w-full h-11 inline-flex items-center justify-center gap-2 bg-[#13AFDC] text-navy font-bold text-base rounded-xl transition-[transform,opacity] duration-200"
              >
                Next →
              </button>
            )}

            {/* Step 2 (mobile) — contact details + submit; always visible on desktop */}
            <div
              data-testid="form-step-2"
              className={step === 2 ? "mobile-step-enter md:block" : "hidden md:block"}
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-3 gap-y-3 mb-3 md:mb-[18px]">
                <div data-error-anchor="name">
                  <label htmlFor="lead-name" className={labelCls}>
                    Full Name
                  </label>
                  <input
                    id="lead-name"
                    data-testid="lead-name-input"
                    type="text"
                    autoComplete="name"
                    value={fields.name}
                    onChange={setField("name")}
                    placeholder="Jane Citizen"
                    className={inputCls}
                  />
                  <InlineError id="name">{errors.name}</InlineError>
                </div>
                <div data-error-anchor="email">
                  <label htmlFor="lead-email" className={labelCls}>
                    Email
                  </label>
                  <input
                    id="lead-email"
                    data-testid="lead-email-input"
                    type="email"
                    autoComplete="email"
                    value={fields.email}
                    onChange={setField("email")}
                    placeholder="jane@email.com"
                    className={inputCls}
                  />
                  <InlineError id="email">{errors.email}</InlineError>
                </div>
                <div data-error-anchor="phone">
                  <label htmlFor="lead-phone" className={labelCls}>
                    Phone
                  </label>
                  <input
                    id="lead-phone"
                    data-testid="lead-phone-input"
                    type="tel"
                    autoComplete="tel"
                    value={fields.phone}
                    onChange={setField("phone")}
                    placeholder="04XX XXX XXX"
                    className={inputCls}
                  />
                  <InlineError id="phone">{errors.phone}</InlineError>
                </div>
              </div>

              <div
                className="mb-3 md:mb-[18px] flex items-start gap-2.5"
                data-error-anchor="consent"
              >
                <input
                  id="lead-consent"
                  data-testid="consent-checkbox"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                    clearError("consent");
                  }}
                  className="mt-0.5 w-4 h-4 shrink-0 accent-[#64B84E] cursor-pointer"
                />
                <label
                  htmlFor="lead-consent"
                  className="text-[11px] md:text-[11.5px] text-white/80 md:text-navy-muted leading-snug cursor-pointer"
                >
                  I consent to C2H Energy contacting me by phone regarding
                  products, services and available incentives under the
                  Victorian Energy Upgrades (VEU) program.
                </label>
              </div>
              <InlineError id="consent">{errors.consent}</InlineError>

              {errorMsg && (
                <p
                  data-testid="form-error-message"
                  className="mb-3 md:mb-[18px] text-sm font-medium text-red-300 md:text-red-600"
                  role="alert"
                >
                  {errorMsg}
                </p>
              )}

              {step === 2 && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  data-testid="lead-back-button"
                  className="md:hidden mb-2.5 text-[13px] font-semibold text-white/70"
                >
                  ← Back
                </button>
              )}

              <button
                type="submit"
                disabled={status === "submitting"}
                data-testid="lead-submit-button"
                className="mb-3 w-full h-[46px] md:h-12 inline-flex items-center justify-center gap-3 bg-amber text-navy-deep font-bold text-lg px-8 rounded-xl hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-[transform,opacity] duration-200"
              >
                {status === "submitting" && (
                  <Loader2 className="w-5 h-5 animate-spin" />
                )}
                {status === "submitting" ? "Sending…" : "Get My Rinnai Quote"}
              </button>

              <p
                data-testid="form-countdown-line"
                className="flex items-center justify-center gap-1.5 text-center text-[11px] md:text-[11.5px] text-white/70 md:text-navy-muted md:whitespace-nowrap"
              >
                <ShieldCheck className="w-3.5 h-3.5 text-white/70 md:text-gdark shrink-0" />
                <span>
                  {RULES_DAYS} days until Victoria&apos;s gas hot water rules
                  change
                </span>
                <span aria-hidden="true">·</span>
                <span data-testid="hero-card-trust-line">
                  Free quote · No spam · No obligation
                </span>
              </p>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeroEligibilityCard;
